import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ImportChargeFiltersScreen.module.scss';
import Button from '../../../../common/components/button/Button';
import FormItem from '../../../../common/components/formItem/FormItem';
import Label from '../../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../../common/types/SelectValueLabel';
import SuppliersService from '../../../../api/suppliers/SuppliersService';
import Select from '../../../../common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import Loading from '../../../../common/services/Loading';
import ReactTooltip from 'react-tooltip';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import moment from 'moment';
import { ProcessStatus } from 'api/import-charges/enums/ProcessStatus';

export interface Filters {
    startDate?: Date | null,
    endDate?: Date | null,
    supplierId?: string;
    supplierName?: string;
    page?: number;
    orderColumn?: string;
    orderBy?: string;
    allIn?: string;
    status?: ProcessStatus
}

type Props = {
    filters: Filters;
    onFilter: (filters: Filters) => void;
    load?: boolean;
    onChange: (filters: Filters) => void;
}

const ImportChargeFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, watch, getValues, control } = form;

    const [suppliersOptions, setSuppliers] = useState<SelectValueLabel[]>([]);
    const [statusOptions] = useState<SelectValueLabel[]>([{
        label: t(('import_charges.process_status.OK') as any),
        value: ProcessStatus.OK
    },
    {
        label: t(('import_charges.process_status.PENDING') as any),
        value: ProcessStatus.PENDING
    },
    {
        label: t(('import_charges.process_status.INVALID') as any),
        value: ProcessStatus.INVALID
    },
    {
        label: t(('import_charges.process_status.VALIDATED') as any),
        value: ProcessStatus.VALIDATED
    }]);
    const [reRender, setReRender] = useState<number>(0);

    const getData = async () => {
        try {
            Loading.show();
            const [suppliersOptions] = await Promise.all([
                SuppliersService.catalog({ isCharges: true }),
            ]);

            setSuppliers(suppliersOptions);

            Loading.hide();
        } catch (error) {
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        ReactTooltip.rebuild()
    }, []);
    const clearFilters = () => {
        form.reset({
            startDate: null,
            endDate: null,
            supplierId: undefined,
            supplierName: undefined
        });
        onInputChange();
        onSubmit(getValues());
    }

    const onSubmit = (filters: Filters) => {
        onFilter({ ...filters });
    }

    const onInputChange = () => {
        onChange(getValues());
        setReRender(reRender + 1);
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row key={'chargesFiltersRow' + reRender}>
                            <Col xs={12}>
                                <RangeInputPicker
                                    label={t('import_charges.date_importation')}
                                    onChange={(startValue: any, endValue: any) => {
                                        // Limites do dia no fuso do utilizador: o backend usa-os tal como vêm.
                                        setValue('startDate', startValue ? moment(startValue).startOf('day').toDate() : undefined);
                                        setValue('endDate', endValue ? moment(endValue).endOf('day').toDate() : undefined);
                                        onInputChange();

                                    }}
                                    defaultStartValue={getValues('startDate')}
                                    defaultEndValue={getValues('endDate')}
                                    isDatePicker
                                />
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.supplier')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={suppliersOptions}
                                                    isClearable
                                                    placeholder={t('common.supplier')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('supplierName', suppliersOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={suppliersOptions.find(x => x.value === value) ? { label: suppliersOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="supplierId"
                                        defaultValue={getValues('supplierId')} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('import_charges.status')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={statusOptions}
                                                    isClearable
                                                    placeholder={t('import_charges.status')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('status', statusOptions.find(x => x.value == data?.value)?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={statusOptions.find(x => x.value === value) ? { label: statusOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="status"
                                        defaultValue={getValues('status')} />
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                </div>
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(ImportChargeFiltersScreen);